import { createTheme } from '@mui/material/styles';

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode
          primary: {
            main: '#40685E', // Cor dos botões
            light: '#557B6E',
            dark: '#2A453E',
          },
          secondary: {
            main: '#40685E',
            light: '#688F84',
            dark: '#2A453E',
          },
          background: {
            default: '#F8F9FA',
            paper: '#FFFFFF',
          },
          text: {
            primary: '#2C3E35', // Verde muito escuro, quase preto
            secondary: '#5C7168', // Verde acinzentado médio
          },
          divider: 'rgba(47, 90, 69, 0.08)', // Verde escuro com baixa opacidade
          success: {
            main: '#4CAF50', // Green
            light: '#81C784',
            dark: '#388E3C',
          },
          warning: {
            main: '#FF9800', // Orange
            light: '#FFB74D',
            dark: '#F57C00',
          },
          error: {
            main: '#F44336', // Red
            light: '#E57373',
            dark: '#D32F2F',
          },
        }
      : {
          // Dark mode
          primary: {
            main: '#40685E', // Mesma cor dos botões no dark mode
            light: '#688F84',
            dark: '#2A453E',
          },
          secondary: {
            main: '#5E8775', // Verde sage médio
            light: '#7FA08C',
            dark: '#456357',
          },
          background: {
            default: '#1A1C1B',
            paper: '#222524',
          },
          text: {
            primary: '#B7C9C0', // Verde sage claro
            secondary: 'rgba(183, 201, 192, 0.7)', // Verde sage com opacidade
          },
          divider: 'rgba(127, 182, 159, 0.08)', // Verde sage com baixa opacidade
          success: {
            main: '#4CAF50', // Green
            light: '#81C784',
            dark: '#388E3C',
          },
          warning: {
            main: '#FF9800', // Orange
            light: '#FFB74D',
            dark: '#F57C00',
          },
          error: {
            main: '#F44336', // Red
            light: '#E57373',
            dark: '#D32F2F',
          },
        }),
  },
  typography: {
    fontFamily: '"Space Grotesk", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: '#40685E', // Cor para o texto PartnerSafe
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.6,
    },
    // Adicionando estilo específico para o texto do logo
    logo: {
      color: '#40685E',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          backgroundColor: '#40685E',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#2A453E',
          },
        },
        contained: {
          backgroundColor: '#40685E',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#2A453E',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(64, 104, 94, 0.5)',
          },
        },
        outlined: {
          borderColor: '#40685E',
          color: '#40685E',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(64, 104, 94, 0.04)',
            borderColor: '#2A453E',
          },
        },
        text: {
          color: '#40685E',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(64, 104, 94, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px 0 rgba(47, 90, 69, 0.05)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px 0 rgba(47, 90, 69, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: mode === 'light' 
            ? '1px solid rgba(47, 90, 69, 0.08)'
            : '1px solid rgba(127, 182, 159, 0.08)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 500,
            background: mode === 'light' 
              ? 'rgba(47, 90, 69, 0.03)'
              : 'rgba(127, 182, 159, 0.03)',
            color: mode === 'light' ? '#2C3E35' : '#B7C9C0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: mode === 'light'
            ? '1px solid rgba(47, 90, 69, 0.08)'
            : '1px solid rgba(127, 182, 159, 0.08)',
          padding: 16,
          color: mode === 'light' ? '#2C3E35' : '#B7C9C0',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: mode === 'light'
              ? 'rgba(47, 90, 69, 0.03)'
              : 'rgba(127, 182, 159, 0.03)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          background: mode === 'light' ? '#FFFFFF' : '#222524',
          boxShadow: mode === 'light'
            ? '0 2px 12px 0 rgba(47, 90, 69, 0.08)'
            : '0 2px 12px 0 rgba(0, 0, 0, 0.2)',
          border: mode === 'light'
            ? '1px solid rgba(47, 90, 69, 0.08)'
            : '1px solid rgba(127, 182, 159, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: mode === 'light' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
            '&:hover': {
              background: mode === 'light' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.03)',
            },
            '& fieldset': {
              borderColor: mode === 'light'
                ? 'rgba(47, 90, 69, 0.15)'
                : 'rgba(127, 182, 159, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: mode === 'light'
                ? 'rgba(47, 90, 69, 0.25)'
                : 'rgba(127, 182, 159, 0.25)',
            },
            '&.Mui-focused fieldset': {
              borderColor: mode === 'light' ? '#40685E' : '#7FB69F',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: mode === 'light' ? '#40685E' : '#7FB69F',
          '&:hover': {
            background: mode === 'light'
              ? 'rgba(47, 90, 69, 0.05)'
              : 'rgba(127, 182, 159, 0.05)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' 
            ? 'rgba(64, 104, 94, 0.08)'
            : 'rgba(127, 182, 159, 0.08)',
          color: mode === 'light' ? '#40685E' : '#7FB69F',
        },
        deleteIcon: {
          color: mode === 'light' ? '#40685E' : '#7FB69F',
          '&:hover': {
            color: mode === 'light' ? '#2A453E' : '#5E8775',
          },
        },
      },
    },
  },
});

export const createAppTheme = (mode) => createTheme(getDesignTokens(mode)); 